import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { ResumeIndicatorB4ByHealthTeamData } from "@/services/esus/indicator/esb/b4/getResumeIndicatorB4ByHealthTeam";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getIndicatorClassification, IndicatorEsbEnum } from "@/utils/indicatorsEsb";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { IndicatorEsbB4HealthTeamsFilters } from "./IndicatorEsbB4HealthTeamsFilters";
import { IndicatorEsbB4HealthTeamsClassificationTable } from "./IndicatorEsbB4HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsbB4HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorB4ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorB4ByHealthTeam: () => Promise<ResumeIndicatorB4ByHealthTeamData[]>;
};

export function IndicatorEsbB4HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorB4ByHealthTeam,
}: Readonly<IndicatorEsbB4HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorB4ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorB4AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Atividades coletivas por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorB4AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorB4ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe ESB": healthTeamClassification.esbHealthTeamName,
					"Equipe ESB INE": healthTeamClassification.esbHealthTeamIne,
					"Equipe ESF": healthTeamClassification.linkedHealthTeamName,
					"Equipe ESF INE": healthTeamClassification.linkedHealthTeamIne,
					"Cidadãos vinculados com escovação supervisionada":
						healthTeamClassification.totalCitizensFinishedLinked ?? 0,
					"Cidadãos não vinculados com escovação supervisionada":
						(healthTeamClassification.totalCitizensFinished ?? 0) -
						(healthTeamClassification.totalCitizensFinishedLinked ?? 0),
					"Cidadãos com escovação supervisionada":
						healthTeamClassification.totalCitizensFinished,
					"Cidadãos vinculados": healthTeamClassification.totalCitizens,
					"% Classificação": `${calculatePercentage(
						healthTeamClassification.totalCitizensFinished,
						healthTeamClassification.totalCitizens
					)}%`,
					Classificação: getIndicatorClassification(
						IndicatorEsbEnum.B4,
						calculatePercentage(
							healthTeamClassification.totalCitizensFinished,
							healthTeamClassification.totalCitizens
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsbB4HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsbB4HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
